import { addDays, APP_TIMEZONE, localDateKey, weekStart } from "./time";

// Habit streaks (HB-2, project-description.md §8): consecutive periods that
// meet the target, forgiving one missed day per ISO week when the habit
// allows a grace day. Pure and offline (D51).

export type StreakHabit = {
	cadence: "daily" | "weekly";
	target_per_period: number;
	grace_days_per_week: number;
	is_minimum: boolean;
	created_at: string;
};

export type StreakLog = { date: string; count: number };

export type Streak = {
	/** Periods in the current run (days or weeks). */
	current: number;
	best: number;
	/** Count logged in the current period (today, or this week). */
	periodCount: number;
	/** True when the current period already meets the target. */
	periodMet: boolean;
	/** Whether the habit asks for anything this period (false on a minimum day for non-minimum habits). */
	due: boolean;
};

/**
 * @param minimumDays days the owner switched to minimum-viable-day mode (HB-3):
 *   on those days only minimum habits count; others are neither met nor missed.
 */
export function computeStreak(
	habit: StreakHabit,
	logs: StreakLog[],
	today: string,
	minimumDays: ReadonlySet<string> = new Set(),
	timeZone = APP_TIMEZONE,
): Streak {
	const counts = new Map<string, number>();
	for (const l of logs) counts.set(l.date, (counts.get(l.date) ?? 0) + l.count);
	const start = localDateKey(new Date(habit.created_at), timeZone);
	const target = Math.max(1, habit.target_per_period);
	return habit.cadence === "weekly"
		? weekly(counts, start, today, target)
		: daily(habit, counts, start, today, target, minimumDays);
}

function daily(
	habit: StreakHabit,
	counts: Map<string, number>,
	start: string,
	today: string,
	target: number,
	minimumDays: ReadonlySet<string>,
): Streak {
	const neutral = (d: string) => minimumDays.has(d) && !habit.is_minimum;
	let run = 0;
	let best = 0;
	let graceWeek = "";
	let graceUsed = 0;

	// Logs before the habit existed (e.g. backfilled) still count from their first day.
	const firstLog = [...counts.keys()].filter((d) => (counts.get(d) ?? 0) > 0).sort()[0];
	let d = firstLog && firstLog < start ? firstLog : start;

	for (; d < today; d = addDays(d, 1)) {
		if (neutral(d)) continue;
		if ((counts.get(d) ?? 0) >= target) {
			run++;
			best = Math.max(best, run);
			continue;
		}
		const week = weekStart(d);
		if (week !== graceWeek) {
			graceWeek = week;
			graceUsed = 0;
		}
		if (run > 0 && graceUsed < habit.grace_days_per_week) {
			graceUsed++; // forgiven: the run continues but this day does not add to it
			continue;
		}
		run = 0;
	}

	const periodCount = counts.get(today) ?? 0;
	const periodMet = periodCount >= target;
	// Today never breaks a run: the day is not over yet.
	const current = periodMet && !neutral(today) ? run + 1 : run;
	return { current, best: Math.max(best, current), periodCount, periodMet, due: !neutral(today) };
}

function weekly(counts: Map<string, number>, start: string, today: string, target: number): Streak {
	const sumWeek = (monday: string) => {
		let sum = 0;
		for (let i = 0; i < 7; i++) sum += counts.get(addDays(monday, i)) ?? 0;
		return sum;
	};
	const thisWeek = weekStart(today);
	let run = 0;
	let best = 0;
	for (let w = weekStart(start); w < thisWeek; w = addDays(w, 7)) {
		if (sumWeek(w) >= target) {
			run++;
			best = Math.max(best, run);
		} else {
			run = 0;
		}
	}
	const periodCount = sumWeek(thisWeek);
	const periodMet = periodCount >= target;
	const current = periodMet ? run + 1 : run;
	return { current, best: Math.max(best, current), periodCount, periodMet, due: true };
}
